import React, { Component } from "react";
import AppointmentTooltip from "./AppointmentTooltip";
import "./AppointmentBooking.scss";
import { AppointmentForm } from "./AppointmentForm";
import moment from "moment";
import outstanding from "assets/images/app-icons/0.png";
import req_therapist from "assets/images/app-icons/1.png";
import reschedule from "assets/images/app-icons/8.png";

const DEFAULT_AVATAR =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23e0e0e0'/%3E%3Cpath d='M50 50c8.284 0 15-6.716 15-15s-6.716-15-15-15-15 6.716-15 15 6.716 15 15 15zm0 7.5c-10 0-30 5-30 15v7.5h60v-7.5c0-10-20-15-30-15z' fill='%23999'/%3E%3C/svg%3E";

class AppointmentBooking extends Component {
  constructor(props) {
    super(props);

    this.state = {
      timeSlots: this.generateTimeSlots(),
      showPopup: false,
      showTooltip: false,
      selectedSlot: null,
      selectedAppointmentIndex: null,
      tooltipData: null,
      tooltipPosition: { top: 0, left: 0 },
      tooltipPlacement: "top",
      lastTap: 0,
      lastClick: 0,
      touchStartY: 0,
      touchStartX: 0,
      showCreateAppointment: false,
      selectedStaffId: null,
      selectedTimeSlot: null,
      selectedAppointment: null,
    };

    this.containerRef = React.createRef();
    this.timeScrollRef = React.createRef();
    this.headerScrollRef = React.createRef();
    this.staffContentRef = React.createRef();
    this.staffScrollRef = React.createRef();

    this.syncingScroll = false;
  }

  generateTimeSlots = () => {
    const slots = [];
    const startHour = 9;
    const endHour = 20;

    for (let hour = startHour; hour < endHour; hour++) {
      const period = hour < 12 ? "AM" : "PM";
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;

      slots.push({
        start: `${hour.toString().padStart(2, "0")}:00`,
        display: `${displayHour}:00 ${period}`,
        hour: hour,
      });
    }

    return slots;
  };

  componentDidMount() {
    if (this.timeScrollRef.current) {
      this.timeScrollRef.current.scrollTop = 0;
    }

    if (this.staffContentRef.current) {
      this.staffContentRef.current.scrollTop = 0;
      this.staffContentRef.current.scrollLeft = 0;
    }

    if (this.headerScrollRef.current) {
      this.headerScrollRef.current.addEventListener(
        "touchstart",
        this.handleHeaderTouchStart,
        { passive: true }
      );
      this.headerScrollRef.current.addEventListener(
        "touchmove",
        this.handleTouchMove,
        { passive: false }
      );
    }

    window.scrollTo(0, 0);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.startDayHour !== this.props.startDayHour ||
      prevProps.endDayHour !== this.props.endDayHour
    ) {
      this.setState({ timeSlots: this.generateTimeSlots() });
    }

    // Scroll to top when date changes
    if (prevProps.currentDate !== this.props.currentDate) {
      if (this.timeScrollRef.current) {
        this.timeScrollRef.current.scrollTop = 0;
      }

      if (this.staffContentRef.current) {
        this.staffContentRef.current.scrollTop = 0;
      }
    }
  }

  formatTime = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  getAppointmentColor = (appointment) => {
    const color =
      appointment.color ||
      appointment.backgroundColor ||
      appointment.bg_color ||
      appointment.appointmentColor ||
      "#fce7f3";

    return color;
  };

  getContrastColor = (hexColor) => {
    const hex = hexColor.replace("#", "");

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  };

  getBorderColor = (hexColor) => {
    const hex = hexColor.replace("#", "");

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const lighterR = Math.min(255, Math.floor(r + (255 - r) * 0.4));
    const lighterG = Math.min(255, Math.floor(g + (255 - g) * 0.4));
    const lighterB = Math.min(255, Math.floor(b + (255 - b) * 0.4));

    return `#${lighterR.toString(16).padStart(2, "0")}${lighterG
      .toString(16)
      .padStart(2, "0")}${lighterB.toString(16).padStart(2, "0")}`;
  };

  formatTime24 = (date) => {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  getAppointmentsForStaff = (staffId) => {
    const { dataSource = [], staffList = [] } = this.props;
    const startHour = 9;
    const endHour = 20;

    const SLOT_HEIGHT_PX = 75;
    const PIXELS_PER_MINUTE = SLOT_HEIGHT_PX / 60;

    const staff = staffList.find((s) => s.id === staffId);

    const filtered = dataSource
      .filter((event) => {
        const eventStaffId = event.staffId || event.staff_id || event.id;
        return eventStaffId === staffId;
      })
      .map((event) => {
        const eventStartDate = new Date(
          event.startDate || event.start_date || event.date
        );
        const eventEndDate = new Date(event.endDate || event.end_date);

        const dayStart = new Date(
          eventStartDate.getFullYear(),
          eventStartDate.getMonth(),
          eventStartDate.getDate(),
          startHour,
          0,
          0
        );

        const dayEnd = new Date(
          eventStartDate.getFullYear(),
          eventStartDate.getMonth(),
          eventStartDate.getDate(),
          endHour,
          0,
          0
        );

        const effectiveStart = new Date(Math.max(eventStartDate, dayStart));
        const effectiveEnd = new Date(Math.min(eventEndDate, dayEnd));

        // Calculate minutes from day start
        const startMinutesFromDayStart = (effectiveStart - dayStart) / 60000;
        const durationMinutes = (effectiveEnd - effectiveStart) / 60000;

        // Calculate px positions based on 115px per hour
        const topPx = startMinutesFromDayStart * PIXELS_PER_MINUTE;
        const heightPx = durationMinutes * PIXELS_PER_MINUTE;

        return {
          id: event.appointmentId || event.appointment_id || event.id,
          custName: event.cust_name || "",
          cust_refer: event.cust_refer || "",
          appointmentText: event.text || "",
          staffName: staff ? staff.staff_name : "",
          remark_val: event?.remark_val || "",
          sec_status_flag: event?.sec_status_flag,
          outstanding: event?.outstanding,
          outstanding_amt: event?.outstanding_amt,
          req_therapist: event?.req_therapist,
          reason:
            event.serviceName ||
            event.service_name ||
            event.description ||
            event.reason ||
            "Appointment",
          notes: event.notes || event.remarks || "",
          startTime: effectiveStart,
          endTime: effectiveEnd,
          startTimeFormatted: this.formatTime24(effectiveStart),
          endTimeFormatted: this.formatTime24(effectiveEnd),
          startMinutesFromDayStart: startMinutesFromDayStart,
          durationMinutes: durationMinutes,
          topPx: topPx,
          heightPx: heightPx,
          color:
            event.color ||
            event.backgroundColor ||
            event.bg_color ||
            event.appointmentColor,
          originalData: event,
        };
      })
      .sort((a, b) => a.startMinutesFromDayStart - b.startMinutesFromDayStart);

    return this.calculateAppointmentColumns(filtered);
  };

  calculateAppointmentColumns = (appointments) => {
    const columns = [];

    appointments.forEach((apt) => {
      const aptEnd = apt.startMinutesFromDayStart + apt.durationMinutes;

      let columnIndex = 0;
      while (columnIndex < columns.length) {
        const column = columns[columnIndex];
        const hasOverlap = column.some((existingApt) => {
          const existingEnd =
            existingApt.startMinutesFromDayStart + existingApt.durationMinutes;
          return (
            apt.startMinutesFromDayStart < existingEnd &&
            aptEnd > existingApt.startMinutesFromDayStart
          );
        });

        if (!hasOverlap) {
          column.push(apt);
          apt.column = columnIndex;
          break;
        }
        columnIndex++;
      }

      if (apt.column === undefined) {
        columns.push([apt]);
        apt.column = columnIndex;
      }
    });

    appointments.forEach((apt) => {
      const aptEnd = apt.startMinutesFromDayStart + apt.durationMinutes;

      let maxColumns = 1;
      appointments.forEach((other) => {
        const otherEnd = other.startMinutesFromDayStart + other.durationMinutes;
        if (
          apt.startMinutesFromDayStart < otherEnd &&
          aptEnd > other.startMinutesFromDayStart
        ) {
          maxColumns = Math.max(maxColumns, other.column + 1);
        }
      });

      apt.totalColumns = maxColumns;
    });

    return appointments;
  };

  handleStaffHeaderScroll = (e) => {
    if (this.syncingScroll) return;

    this.syncingScroll = true;

    // Sync horizontal scroll from header to content
    if (this.staffContentRef.current) {
      this.staffContentRef.current.scrollLeft = e.target.scrollLeft;
    }

    setTimeout(() => {
      this.syncingScroll = false;
    }, 0);
  };

  handleStaffScroll = (e) => {
    if (this.syncingScroll) return;

    this.syncingScroll = true;

    // Sync horizontal scroll from content to header
    if (this.headerScrollRef.current) {
      this.headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    }

    // Sync vertical scroll with time column
    if (this.timeScrollRef.current) {
      this.timeScrollRef.current.scrollTop = e.target.scrollTop;
    }

    setTimeout(() => {
      this.syncingScroll = false;
    }, 0);
  };

  handleTimeScroll = (e) => {
    if (this.syncingScroll) return;

    this.syncingScroll = true;

    // Sync vertical scroll with staff content
    if (this.staffContentRef.current) {
      this.staffContentRef.current.scrollTop = e.target.scrollTop;
    }

    setTimeout(() => {
      this.syncingScroll = false;
    }, 0);
  };

  // Mobile: Touch handlers for appointments
  handleTouchStart = (staffId, appointment, event) => {
    console.log(event, "event");
    // if (!event) return;
    this.touchStartY = event.touches[0].clientY;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartTime = new Date().getTime();
  };

  handleTouchEnd = (staffId, appointment, event) => {
    console.log(event, "event");
    // if (!event) return;

    const touchEndY = event.changedTouches[0].clientY;
    const touchEndX = event.changedTouches[0].clientX;
    const touchEndTime = new Date().getTime();

    const moveY = Math.abs(touchEndY - this.touchStartY);
    const moveX = Math.abs(touchEndX - this.touchStartX);
    const touchDuration = touchEndTime - this.touchStartTime;

    if (moveY > 10 || moveX > 10) {
      return;
    }

    if (touchDuration > 500) {
      return;
    }

    event.preventDefault();

    const timeSinceLastTap = touchEndTime - this.state.lastTap;

    if (timeSinceLastTap < 400 && timeSinceLastTap > 0) {
      // Double tap detected
      this.setState({
        lastTap: 0,
        showTooltip: false,
        tooltipData: null,
      });

      if (appointment && this.props.onAppointmentDblClick) {
        // this.props.onAppointmentDblClick({
        //   appointmentData: appointment.originalData,
        //   cancel: false,
        // });
      }
    } else {
      // Single tap
      this.setState({ lastTap: touchEndTime });

      if (appointment) {
        const target = event.currentTarget;
        if (target) {
          this.showTooltip(appointment, target);
        }
      }
    }
  };

  handleHeaderTouchStart = (e) => {
    console.log(e, "E");
    if (!e) return;
    this.touchStartX = e.touches[0].clientX;
    this.touchStartScrollLeft = this.headerScrollRef.current?.scrollLeft || 0;
  };

  handleTouchMove = (e) => {
    if (!this.headerScrollRef.current) return;

    const touchX = e.touches[0].clientX;
    const deltaX = this.touchStartX - touchX;

    this.headerScrollRef.current.scrollLeft =
      this.touchStartScrollLeft + deltaX;
  };

  handleAppointmentDoubleClick = (staffId, appointment, event) => {
    event.stopPropagation();

    this.setState({
      showTooltip: false,
      tooltipData: null,
    });

    console.log(appointment, "Appointment");
    let getStartTime = null;

    getStartTime = appointment.startTimeFormatted.split(":")[0];

    if (appointment && this.props.onAppointmentDblClick) {
      this.setState({
        showCreateAppointment: true,
        selectedAppointment: appointment?.originalData,
        selectedTimeSlot: Number(getStartTime),
        selectedStaffId: appointment?.id,
      });
    }
  };

  handleAppointmentClick = (staffId, appointment, event) => {
    event.stopPropagation();

    const target = event.currentTarget;
    if (target && appointment) {
      this.showTooltip(appointment, target);
    }
  };

  handleSlotTouchStart = (event) => {
    this.touchStartY = event.touches[0].clientY;
    this.touchStartX = event.touches[0].clientX;
    this.touchStartTime = new Date().getTime();
  };

  handleSlotTouchEnd = (staffId, slotHour, event) => {
    const touchEndY = event.changedTouches[0].clientY;
    const touchEndX = event.changedTouches[0].clientX;
    const touchEndTime = new Date().getTime();

    const moveY = Math.abs(touchEndY - this.touchStartY);
    const moveX = Math.abs(touchEndX - this.touchStartX);
    const touchDuration = touchEndTime - this.touchStartTime;

    if (moveY > 10 || moveX > 10) {
      return;
    }

    if (touchDuration > 500) {
      return;
    }

    event.preventDefault();

    const timeSinceLastTap = touchEndTime - this.state.lastTap;

    if (timeSinceLastTap < 400 && timeSinceLastTap > 0) {
      this.setState({
        lastTap: 0,
        showTooltip: false,
        tooltipData: null,
        showCreateAppointment: true,
        selectedStaffId: staffId,
        selectedTimeSlot: slotHour,
      });

      if (this.props.onCellClick) {
        const cellDate = new Date(this.props.currentDate || new Date());
        cellDate.setHours(slotHour, 0, 0, 0);
      }
    } else {
      this.setState({ lastTap: touchEndTime });
    }
  };

  handleSlotDoubleClick = (staffId, slotHour, event) => {
    event.stopPropagation();
    console.log(slotHour, "SlotHour");
    this.setState({
      showTooltip: false,
      tooltipData: null,
      showCreateAppointment: true,
      selectedStaffId: staffId,
      selectedTimeSlot: slotHour,
    });

    if (this.props.onCellClick) {
      const cellDate = new Date(this.props.currentDate || new Date());
      cellDate.setHours(slotHour, 0, 0, 0);
    }
  };

  closeCreateAppointment = () => {
    this.setState({
      showCreateAppointment: false,
      selectedStaffId: null,
      selectedTimeSlot: null,
      selectedAppointment: null,
    });
  };

  showTooltip = (appointment, targetElement) => {
    const rect = targetElement.getBoundingClientRect();
    const containerRect = this.containerRef.current.getBoundingClientRect();

    const relativeTop = rect.top - containerRect.top;
    const relativeLeft = rect.left - containerRect.left + rect.width / 2;

    const estimatedTooltipHeight = 200;
    const tooltipMargin = 20;

    const spaceAbove = relativeTop;
    const hasSpaceAbove = spaceAbove >= estimatedTooltipHeight + tooltipMargin;

    const placement = hasSpaceAbove ? "top" : "bottom";

    let position;
    if (placement === "top") {
      position = { top: relativeTop, left: relativeLeft };
    } else {
      position = { top: relativeTop + rect.height, left: relativeLeft };
    }

    this.setState({
      showTooltip: true,
      tooltipData: appointment,
      tooltipPosition: position,
      tooltipPlacement: placement,
    });
  };

  closeTooltip = () => {
    this.setState({
      showTooltip: false,
      tooltipData: null,
    });
  };

  handleSelectedHour = (startTime) => {
    let { selectedStaffId, selectedTimeSlot } = this.state;
    const hasSelection = selectedStaffId !== null && selectedTimeSlot !== null;
    if (hasSelection) {
      this.setState({
        selectedTimeSlot: Number(startTime),
      });
    }
  };

  handleSelectStaff = (staffId) => {
    let { selectedStaffId, selectedTimeSlot } = this.state;
    const hasSelection = selectedStaffId !== null && selectedTimeSlot !== null;
    if (hasSelection) {
      this.setState({
        selectedStaffId: staffId,
      });
    }
  };

  render() {
    const {
      staffList = [],
      dataSource = [],
      className = "",
      height = "100vh",
      currentDate,
    } = this.props;

    const {
      timeSlots,
      showTooltip,
      tooltipData,
      tooltipPosition,
      tooltipPlacement,
      showCreateAppointment,
      selectedStaffId,
      selectedTimeSlot,
      selectedAppointment,
    } = this.state;

    const staffArray = Array.isArray(staffList) ? staffList : [];
    const formattedDate = currentDate.toLocaleDateString("en-GB");

    const getStaffColumnWidth = () => {
      if (typeof window !== "undefined") {
        if (window.innerWidth <= 768) return 75;
        if (window.innerWidth >= 769) return 240;
      }
      return 200;
    };

    const staffColumnWidth = getStaffColumnWidth();
    const totalStaffWidth = staffArray.length * staffColumnWidth;

    const hasSelection = selectedStaffId !== null && selectedTimeSlot !== null;

    return (
      <div
        className={`appointment-booking ${className} ${
          hasSelection ? "has-selection" : ""
        }`}
      >
        <div className="outer-scroll-wrapper" ref={this.outerScrollRef}>
          <div className="booking-container" ref={this.containerRef}>
            {/* Time Column */}
            <div
              className="time-column"
              ref={this.timeScrollRef}
              onScroll={this.handleTimeScroll}
            >
              <div className="time-header"></div>
              <div className="time-slots-wrapper">
                {timeSlots.map((slot, index) => {
                  const timeParts = slot.display.split(" ");
                  const time = timeParts[0];
                  const period = timeParts[1];
                  return (
                    <div
                      key={index}
                      className={`time-slot ${
                        selectedTimeSlot === slot.hour ? "selected" : ""
                      }`}
                      style={{ cursor: hasSelection ? "pointer" : "" }}
                      onClick={() => this.handleSelectedHour(slot?.hour)}
                    >
                      <div className="time-display">
                        <span className="time-hour">{time}</span>
                        <span className="time-period">{period}</span>
                      </div>
                      <div className="time-intervals">
                        <div className="interval">30</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="staff-scroll-container" ref={this.staffScrollRef}>
              <div
                className={`staff-headers-scroll-wrapper ${
                  hasSelection ? "scrollable" : ""
                }`}
                ref={this.headerScrollRef}
                onScroll={this.handleStaffHeaderScroll}
              >
                <div
                  className="staff-headers-row"
                  style={{
                    width: `${totalStaffWidth}px`,
                  }}
                >
                  {staffArray.length > 0 ? (
                    staffArray.map((staff) => (
                      <div
                        key={`header-${staff.id}`}
                        className={`staff-header ${
                          selectedStaffId === staff.id ? "selected" : ""
                        }`}
                        style={{
                          width: `${staffColumnWidth}px`,
                          minWidth: `${staffColumnWidth}px`,
                          cursor: hasSelection ? "pointer" : "",
                        }}
                        onClick={() => this.handleSelectStaff(staff.id)}
                      >
                        <div className="staff-avatar">
                          <img
                            src={staff.emp_pic || DEFAULT_AVATAR}
                            alt={staff.staff_name}
                            onError={(e) => {
                              e.target.src = DEFAULT_AVATAR;
                            }}
                          />
                        </div>
                        <div className="staff-name">{staff.staff_name}</div>
                      </div>
                    ))
                  ) : (
                    <div className="no-staff-header">No staff members</div>
                  )}
                </div>
              </div>

              {showCreateAppointment ? (
                <AppointmentForm
                  selectedDate={moment(currentDate)
                    .locale("en")
                    .format("YYYY-MM-DD")}
                  selectedTime={
                    selectedTimeSlot
                      ? `${selectedTimeSlot.toString().padStart(2, "0")}:00`
                      : ""
                  }
                  handleSelectedHour={this.handleSelectedHour}
                  selectedAppointment={selectedAppointment}
                  startDayHour={this.props.startDayHour}
                  endDayHour={this.props.endDayHour}
                  staffList={staffList}
                  getAppointmentWithStaff={this.props.getAppointmentWithStaff}
                  handleCloseDialog={this.closeCreateAppointment}
                  selectedStaffId={selectedStaffId}
                  onCancel={this.closeCreateAppointment}
                />
              ) : (
                <div
                  className="staff-content"
                  onScroll={this.handleStaffScroll}
                  ref={this.staffContentRef}
                >
                  <div className="staff-grid">
                    {staffArray.length > 0
                      ? staffArray.map((staff) => {
                          const staffAppointments =
                            this.getAppointmentsForStaff(staff.id);

                          return (
                            <div
                              key={staff.id}
                              className={`staff-column ${
                                selectedStaffId === staff.id ? "selected" : ""
                              }`}
                              style={{
                                width: `${staffColumnWidth}px`,
                                minWidth: `${staffColumnWidth}px`,
                              }}
                            >
                              <div className="staff-slots">
                                {timeSlots.map((slot, slotIndex) => (
                                  <div
                                    key={slotIndex}
                                    className={`appointment-slot ${
                                      selectedStaffId === staff.id &&
                                      selectedTimeSlot === slot.hour
                                        ? "selected"
                                        : ""
                                    }`}
                                    onTouchStart={this.handleSlotTouchStart}
                                    onTouchEnd={(e) =>
                                      this.handleSlotTouchEnd(
                                        staff.id,
                                        slot.hour,
                                        e
                                      )
                                    }
                                    onDoubleClick={(e) =>
                                      this.handleSlotDoubleClick(
                                        staff.id,
                                        slot.hour,
                                        e
                                      )
                                    }
                                  >
                                    <div className="interval-lines">
                                      <div className="interval-line"></div>
                                      <div className="interval-line"></div>
                                      <div className="interval-line"></div>
                                    </div>
                                  </div>
                                ))}

                                {staffAppointments.length > 0 && (
                                  <div className="appointments-overlay">
                                    {staffAppointments.map(
                                      (appointment, index) => {
                                        const widthPercent =
                                          100 / appointment.totalColumns;
                                        const leftPercent =
                                          widthPercent * appointment.column;

                                        const bgColor =
                                          this.getAppointmentColor(appointment);
                                        const textColor =
                                          this.getContrastColor(bgColor);
                                        const borderColor =
                                          this.getBorderColor(bgColor);

                                        return (
                                          <div
                                            key={appointment.id || index}
                                            className="appointment-card"
                                            style={{
                                              top: `${appointment.topPx}px`,
                                              height: `${appointment.heightPx}px`,
                                              left: `${leftPercent}%`,
                                              width: `${widthPercent}%`,
                                              background: bgColor,
                                              borderLeftColor: borderColor,
                                              color: textColor,
                                            }}
                                            onTouchStart={(e) => {
                                              e.stopPropagation();
                                              this.handleTouchStart(
                                                staff.id,
                                                appointment,
                                                e
                                              );
                                            }}
                                            onTouchEnd={(e) => {
                                              e.stopPropagation();
                                              this.handleTouchEnd(
                                                staff.id,
                                                appointment,
                                                e
                                              );
                                            }}
                                            onClick={(e) =>
                                              this.handleAppointmentClick(
                                                staff.id,
                                                appointment,
                                                e
                                              )
                                            }
                                            onDoubleClick={(e) =>
                                              this.handleAppointmentDoubleClick(
                                                staff.id,
                                                appointment,
                                                e
                                              )
                                            }
                                            onContextMenu={(e) => {
                                              e.preventDefault();
                                              this.handleAppointmentDoubleClick(
                                                staff.id,
                                                appointment,
                                                e
                                              );
                                            }}
                                          >
                                            <div className="d-flex">
                                              <div className="appointment-icon">
                                                {appointment.sec_status_flag ? (
                                                  <div className="pt-1">
                                                    <span className="tooltip-img">
                                                      <img
                                                        src={reschedule}
                                                        width="100%"
                                                      />
                                                      <span className="tooltiptext-img">
                                                        <p>{`Reschedule`}</p>
                                                      </span>
                                                    </span>
                                                  </div>
                                                ) : (
                                                  ""
                                                )}
                                                {appointment.outstanding ? (
                                                  <div className="pt-1">
                                                    <span className="tooltip-img">
                                                      <img src={outstanding} />
                                                      <span className="tooltiptext-img">
                                                        <p>{`Outstanding $${appointment.outstanding_amt}`}</p>
                                                      </span>
                                                    </span>
                                                  </div>
                                                ) : (
                                                  ""
                                                )}
                                                {appointment.req_therapist ? (
                                                  <div className="pt-1">
                                                    <span className="tooltip-img">
                                                      <img
                                                        src={req_therapist}
                                                      />
                                                      <span className="tooltiptext-img">
                                                        <p>{`Request Therapist`}</p>
                                                      </span>
                                                    </span>
                                                  </div>
                                                ) : (
                                                  ""
                                                )}
                                              </div>
                                              <div className="appointment-info ml-1">
                                                <div className="info-row">
                                                  <span className="info-text">
                                                    {
                                                      appointment.startTimeFormatted
                                                    }{" "}
                                                    -{" "}
                                                    {
                                                      appointment.endTimeFormatted
                                                    }
                                                  </span>
                                                </div>

                                                {appointment.custName && (
                                                  <div className="info-row">
                                                    <span className="info-text">
                                                      {appointment.custName}
                                                    </span>
                                                  </div>
                                                )}

                                                {appointment.appointmentText && (
                                                  <div className="info-row">
                                                    <span className="info-text">
                                                      {
                                                        appointment.appointmentText
                                                      }
                                                    </span>
                                                  </div>
                                                )}

                                                {appointment.cust_refer && (
                                                  <div className="info-row">
                                                    <span className="info-text">
                                                      {appointment.cust_refer}
                                                    </span>
                                                  </div>
                                                )}

                                                {appointment.remark_val && (
                                                  <div className="info-row">
                                                    <span className="info-text">
                                                      {appointment.remark_val}
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      : ""}
                  </div>
                </div>
              )}
            </div>

            {showTooltip && tooltipData && (
              <AppointmentTooltip
                data={tooltipData}
                position={tooltipPosition}
                placement={tooltipPlacement}
                onClose={this.closeTooltip}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default AppointmentBooking;
